import type { CustomAutoTableFactory, IProps } from "./auto-table.types";
import { TableColumn } from "./auto-table-column";
import { Table } from "../table";
import { AutoTableRow } from "./auto-table-row/auto-table-row";
import { EmptyState } from "../pre-built/empty-state/empty-state";
import { faMagnifyingGlass } from "@fortawesome/pro-duotone-svg-icons";
import { useCallback, useMemo } from "react";
import { AutoTableLoading } from "./auto-table-loading";
import { AutoTableProvider } from "./auto-table-provider";
import { useRowExpansion } from "./auto-table.hooks";
import { createGenericComponentWrapper } from "../../utilities/component-wrapper/component-wrapper";

// Create a generic component wrapper instance
const genericComponentWrapper = createGenericComponentWrapper();

/**
 * Creates a type-safe AutoTable component for displaying tabular data.
 * Supports row clicking, expansion, highlighting, and loading states.
 * @template T - The type of data in each row.
 * @param {T[]} props.rows - Array of data rows to display in the table.
 * @param {function} props.onRowClick - Callback function when a row is clicked.
 * @param {function} props.getRowKey - Function to get a unique key for each row.
 * @param {function} props.getRowLink - Function to get a link URL for each row.
 * @param {React.ReactNode} props.children - Column definitions using AutoTable.Column.
 * @param {React.ReactNode} props.emptyState - Custom empty state component when no rows are found.
 * @param {boolean} props.highlightOnHover - Whether rows should highlight on hover.
 * @param {number} props.loadingRows - Number of skeleton rows to show while loading.
 * @param {object} props.rowExpansion - Configuration for expandable rows.
 * @param {Array} props.highlightedRowsKeys - Array of row keys to highlight.
 * @param {string} props.name - Name prefix for data-testid attributes.
 * @param {number} props.emptyStateHeight - Height of the empty state area.
 * @returns {JSX.Element} The AutoTable component.
 */
function createAutoTable<T = any>() {
    const AutoTableComponent = genericComponentWrapper<CustomAutoTableFactory<T>, IProps<T>>(
        "Table",
        (
            {
                rows,
                onRowClick,
                getRowKey = (row: any) => row.id,
                getRowLink,
                children,
                emptyState,
                highlightOnHover,
                loadingRows,
                rowExpansion,
                highlightedRowsKeys,
                name,
                emptyStateHeight,
                ...otherProps
            },
            ref
        ) => {
            const hasEmptyRows = useMemo(() => rows?.length === 0, [rows]);

            // Default getRowKey function that handles the case when row might have an id property
            const defaultGetRowKey = useCallback((row: T): string | number => {
                if (row && typeof row === "object" && "id" in row) {
                    return (row as any).id;
                }
                // Fallback to index if no id is available
                return Math.random().toString(36);
            }, []);

            const actualGetRowKey = useMemo(() => getRowKey || defaultGetRowKey, [getRowKey, defaultGetRowKey]);

            const rowExpansionInfo = useRowExpansion({
                rowExpansion,
                records: rows ?? undefined,
                getKey: actualGetRowKey,
            });

            return (
                <AutoTableProvider
                    rowExpansion={rowExpansion}
                    highlightedRowsKeys={highlightedRowsKeys}
                    getRowKey={getRowKey}
                    getRowLink={getRowLink}
                >
                    <Table
                        ref={ref}
                        highlightOnHover={
                            !hasEmptyRows &&
                            (highlightOnHover ||
                                (!highlightOnHover &&
                                    (typeof getRowLink !== "undefined" || typeof onRowClick !== "undefined")))
                        }
                        {...otherProps}
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <TableColumn.Provider isBody={false}>{children}</TableColumn.Provider>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            <TableColumn.Provider isBody={true}>
                                {rows === null ? (
                                    <AutoTableLoading loadingRows={loadingRows}>{children}</AutoTableLoading>
                                ) : hasEmptyRows ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={12} align="center" h={emptyStateHeight ?? 500}>
                                            {typeof emptyState !== "undefined" ? (
                                                emptyState
                                            ) : (
                                                <EmptyState
                                                    icon={faMagnifyingGlass}
                                                    title="No Results Found"
                                                    size="100%"
                                                />
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                ) : (
                                    rows?.map((row, index) => (
                                        <AutoTableRow
                                            index={index}
                                            rows={rows}
                                            expansion={rowExpansionInfo}
                                            key={actualGetRowKey(row)}
                                            row={row}
                                            onClick={onRowClick ? (event) => onRowClick?.(row, event) : undefined}
                                            data-testid={name ? `${name}-row` : `row-${index}`}
                                        >
                                            {children}
                                        </AutoTableRow>
                                    ))
                                )}
                            </TableColumn.Provider>
                        </Table.Tbody>
                    </Table>
                </AutoTableProvider>
            );
        }
    );

    AutoTableComponent.Column = TableColumn;
    return AutoTableComponent;
}

// Create the default AutoTable component (backwards compatibility)
const AutoTable = createAutoTable();

// Type-safe generic AutoTable for when you need specific typing
// Usage example:
// type User = { id: string; name: string; email: string };
// const UserTable = createAutoTable<User>();
// <UserTable rows={users} onRowClick={(user) => console.log(user.name)} />
export { AutoTable, createAutoTable };
